import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { definePersistenceEntry, userScopeCleared, type ModulePersistenceEntry } from '@krp/client-runtime'

import { RECENT_LIMIT, rememberedAfter } from './select'

/**
 * Последние выбранные кассиры (не больше пяти, свежий первым): поиск предлагает их, пока текст не набран. Это предпочтение
 * человека за этим устройством (срок `device`, по пользователю): после обновления страницы список остаётся, чужому он не
 * достаётся. Только идентификаторы — имена берутся из справочника кассиров.
 */
export interface RecentCashiersState {
  ids: string[]
}

export const initialRecentCashiers: RecentCashiersState = { ids: [] }

const recentSlice = createSlice({
  name: 'schedule/recent',
  initialState: initialRecentCashiers,
  reducers: {
    cashierRemembered(state, action: PayloadAction<string>) {
      state.ids = rememberedAfter(state.ids, action.payload)
    },
    recentCashiersRestored(state, action: PayloadAction<string[]>) {
      state.ids = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialRecentCashiers)
  },
})

export const { cashierRemembered, recentCashiersRestored } = recentSlice.actions
export const recentCashiersReducer = recentSlice.reducer

/** Проверка прочитанного из хранилища: массив коротких непустых строк; лишнее и повторы отбрасываются. */
export function decodeRecentCashiers(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const ids: string[] = []
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'string' || entry.trim() === '' || entry.length > 64) return undefined
    if (!ids.some((id) => id.toLowerCase() === entry.toLowerCase())) ids.push(entry)
  }
  return ids.slice(0, RECENT_LIMIT)
}

/** Запись реестра хранения: последние выбранные кассиры (срок `device`, по пользователю). */
export function recentCashiersPersistence<S extends { recent: RecentCashiersState }>(): ModulePersistenceEntry<S> {
  return definePersistenceEntry<S, string[]>({
    key: 'schedule.recent-cashiers',
    slice: 'recent',
    lifetime: 'device',
    scope: 'user',
    version: 1,
    pick: (state) => state.recent.ids,
    decode: decodeRecentCashiers,
    restore: recentCashiersRestored,
  })
}
